import React, { useId, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import type {
  RoleplayPerformanceGroup, RoleplayPerformanceReport, RoleplayPerformanceTrendMetric,
} from 'services/api/adminContentClient';

const groupKey = (group: RoleplayPerformanceGroup) => JSON.stringify([group.mode, group.input_mode, group.provider, group.model]);
const groupLabel = (group: RoleplayPerformanceGroup) =>
  `${group.mode === 'voice' ? (group.input_mode === 'voice' ? 'Spoken Voice' : 'Typed Voice') : 'Text'} · ${group.provider} · ${group.model ?? 'Unknown model'}`;
const seconds = (value: number | null | undefined) => value == null ? null : value / 1000;
const displaySeconds = (value: number | null) => value == null ? 'Unavailable' : `${value.toFixed(2)} s`;
const utc = (value: string) => `${value.slice(0, 10)} ${value.slice(11, 16)} UTC`;
const metrics: Array<{ key: RoleplayPerformanceTrendMetric; label: string }> = [
  { key: 'first_response_ms', label: 'First response' },
  { key: 'controls_ready_ms', label: 'Controls ready' },
  { key: 'server_evaluation_ms', label: 'Server evaluation' },
  { key: 'local_speech_stop_to_audio_ms', label: 'Estimated speech end → first audio' },
  { key: 'local_speech_stop_to_provider_ms', label: 'Speech end → provider event (estimated)' },
  { key: 'elevenlabs_first_audio_ms', label: 'ElevenLabs synthesis + delivery' },
];

type Point = {
  started_at: string; ended_at: string; partial: boolean; count: number; error_count: number;
  p50: number | null; p95: number | null; samples: number; timestamp: number;
};

const TrendTooltip = ({ active, payload, volume = false }: {
  active?: boolean; payload?: Array<{ payload?: Point }>; volume?: boolean;
}) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return <div className="max-w-xs rounded-xl border border-border bg-background p-3 text-xs text-foreground shadow-md">
    <p className="font-medium">{utc(point.started_at)} – {utc(point.ended_at)}</p>
    {point.partial && <p>Partial interval</p>}
    {volume ? <><p>{point.count} recorded attempts</p><p>{point.error_count} errors</p></> : <>
      <p>Median (p50): {displaySeconds(point.p50)}</p><p>p95: {displaySeconds(point.p95)}</p>
      <p>{point.samples} timing samples</p>
    </>}
  </div>;
};

export type RoleplayTrendSelection = { group: string; metric: RoleplayPerformanceTrendMetric };

const RoleplayPerformanceTrends = ({ report, selection, onSelectionChange }: {
  report: RoleplayPerformanceReport;
  selection?: RoleplayTrendSelection;
  onSelectionChange?: (selection: RoleplayTrendSelection) => void;
}) => {
  const id = useId();
  const [localSelection, setLocalSelection] = useState<RoleplayTrendSelection>({ group: '', metric: 'first_response_ms' });
  const currentSelection = selection ?? localSelection;
  const { group: selectedGroup, metric: selectedMetric } = currentSelection;
  const updateSelection = (patch: Partial<RoleplayTrendSelection>) => {
    const next = { ...currentSelection, ...patch };
    setLocalSelection(next);
    onSelectionChange?.(next);
  };
  const group = report.groups.find((item) => groupKey(item) === selectedGroup) ?? report.groups[0];
  if (!group) return null;
  const availableMetrics = metrics.filter(({ key }) =>
    !key.startsWith('local_') || (group.mode === 'voice' && group.input_mode === 'voice'))
    .filter(({ key }) => !key.startsWith('elevenlabs_') || (group.mode === 'voice' && group.provider === 'elevenlabs'));
  const chosenMetric = availableMetrics.find(({ key }) => key === selectedMetric) ?? availableMetrics[0];
  const points: Point[] = (group.trend ?? []).map((bucket) => ({
    ...bucket,
    timestamp: (Date.parse(bucket.started_at) + Date.parse(bucket.ended_at)) / 2,
    p50: seconds(bucket.metrics[chosenMetric.key]?.p50_ms),
    p95: seconds(bucket.metrics[chosenMetric.key]?.p95_ms),
    samples: bucket.metrics[chosenMetric.key]?.count ?? 0,
  }));
  const measuredPoints = points.filter((point) => point.samples > 0);
  const axisLabel = (timestamp: number) => {
    const value = new Date(timestamp).toISOString();
    return report.trend_interval === 'hour' ? `${value.slice(5, 10)} ${value.slice(11, 16)}` : value.slice(5, 10);
  };
  const timeDomain = points.length ? [Date.parse(points[0].started_at), Date.parse(points[points.length - 1].ended_at)] : [0, 1];
  const tick = { fontSize: 11, fill: 'var(--foundation-base-darkbase-dark-400)' };
  const clock = chosenMetric.key === 'first_response_ms'
    ? group.mode !== 'voice' ? 'Submit to visible reply.' : group.input_mode === 'voice'
      ? 'Provider speech-stop event received to first audio. Earlier speech detection and transport are excluded.'
      : 'Typed submit to first audio.'
    : chosenMetric.key === 'controls_ready_ms' ? 'Includes reply playback, when present, and the wait until input is available.'
      : chosenMetric.key === 'server_evaluation_ms' ? 'Combined logged evaluation calls for the learner turn, including retries. Server phases may overlap.'
        : chosenMetric.key === 'elevenlabs_first_audio_ms' ? 'Text sent to ElevenLabs to first audio received. Synthesis and network time are combined.'
          : chosenMetric.key === 'local_speech_stop_to_audio_ms' ? 'Estimated local microphone speech end to first audio. This is not an exact physical speech boundary.'
            : 'Estimated local speech end to provider event received. Includes detection, transport and browser scheduling.';

  return <section aria-label="Roleplay performance trends" className="min-w-0 space-y-4 rounded-2xl border border-border/60 bg-background/30 p-3 sm:p-5">
    <div>
      <h3 className="text-base font-semibold text-foreground">Trends over time</h3>
      <p className="mt-1 text-xs text-muted-foreground">Compare recorded attempts within one mode, input type, provider and model. All intervals use UTC.</p>
    </div>
    <div className="grid min-w-0 gap-3 lg:grid-cols-2">
      <div className="min-w-0 space-y-1.5">
        <label id={`${id}-group-label`} className="text-sm font-medium">Mode, provider and model</label>
        <Select value={groupKey(group)} onValueChange={(value) => updateSelection({ group: value })}>
          <SelectTrigger aria-labelledby={`${id}-group-label`} className="min-w-0 text-left [&>span]:truncate"><SelectValue /></SelectTrigger>
          <SelectContent>{report.groups.map((item) => <SelectItem key={groupKey(item)} value={groupKey(item)}>{groupLabel(item)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="min-w-0 space-y-1.5">
        <label id={`${id}-metric-label`} className="text-sm font-medium">Timing</label>
        <Select value={chosenMetric.key} onValueChange={(value) => updateSelection({ metric: value as RoleplayPerformanceTrendMetric })}>
          <SelectTrigger aria-labelledby={`${id}-metric-label`} className="min-w-0 text-left [&>span]:truncate"><SelectValue /></SelectTrigger>
          <SelectContent>{availableMetrics.map(({ key, label }) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
    <p className="text-xs text-muted-foreground">{clock}</p>
    {points.length === 0 ? <p className="text-sm">Trend data is unavailable for this report. Refresh after the updated server is deployed.</p> : <>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">{chosenMetric.label} · seconds</h4>
          <div className="flex gap-4 text-xs" aria-label="Response-time legend">
            <span className="inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-foundation-base-darkbase-dark-500" />Median (p50)</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed border-foundation-orange-500" />p95</span>
          </div>
        </div>
        {measuredPoints.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No timing samples for this metric in the selected window.</p> : <>
          <div className="mt-3 h-60 min-w-0 w-full" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={points} margin={{ top: 8, right: 12, left: -12, bottom: 4 }}>
                <CartesianGrid stroke="var(--foundation-base-darkbase-dark-200)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="timestamp" type="number" scale="time" domain={timeDomain} tickFormatter={axisLabel} tick={tick} minTickGap={30} />
                <YAxis domain={[0, 'auto']} tick={tick} tickFormatter={(value: number) => `${value}s`} width={54} />
                <Tooltip content={<TrendTooltip />} />
                <Line dataKey="p50" name="Median (p50)" type="linear" stroke="var(--foundation-base-darkbase-dark-500)" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
                <Line dataKey="p95" name="p95" type="linear" stroke="var(--foundation-orange-500)" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {measuredPoints.length === 1 && <p className="text-xs text-muted-foreground">Only one interval has timing samples. More recorded attempts are needed to show a trend.</p>}
        </>}
      </div>
      <div className="min-w-0 border-t border-border/60 pt-4">
        <h4 className="text-sm font-semibold">Recorded attempts and errors</h4>
        <p className="mt-1 text-xs text-muted-foreground">Counts for the selected group. Errors are included in attempts; missing uploads are not captured.</p>
        <div className="mt-3 h-40 min-w-0 w-full" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={points} margin={{ top: 8, right: 12, left: -12, bottom: 4 }}>
              <CartesianGrid stroke="var(--foundation-base-darkbase-dark-200)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="timestamp" type="number" scale="time" domain={timeDomain} tickFormatter={axisLabel} tick={tick} minTickGap={30} />
              <YAxis allowDecimals={false} tick={tick} width={54} />
              <Tooltip content={<TrendTooltip volume />} />
              <Bar dataKey="count" name="Attempts" fill="var(--foundation-base-darkbase-dark-500)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="error_count" name="Errors" fill="hsl(var(--destructive))" radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{report.trend_interval === 'hour' ? 'Hourly' : 'Daily'} intervals · UTC</span>
          <div className="flex gap-4"><span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-foundation-base-darkbase-dark-500" />Attempts</span><span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-destructive" />Errors</span></div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Gaps mean no timing samples, not zero latency. Small samples can vary substantially. The first and last intervals may be partial.</p>
      <details className="min-w-0 text-sm">
        <summary className="cursor-pointer font-medium">View trend data table</summary>
        <div className="mt-3 min-w-0">
          <Table aria-label="Selected roleplay trend data">
            <caption className="p-2 text-left text-xs text-muted-foreground">{groupLabel(group)} · {chosenMetric.label}. Interval boundaries are UTC.</caption>
            <TableHeader><TableRow><TableHead>Interval (UTC)</TableHead><TableHead>Median (p50)</TableHead><TableHead>p95</TableHead><TableHead>Timing samples</TableHead><TableHead>Attempts</TableHead><TableHead>Errors</TableHead></TableRow></TableHeader>
            <TableBody>{points.map((point) => <TableRow key={point.started_at}>
              <TableCell className="whitespace-nowrap">{utc(point.started_at)} – {utc(point.ended_at)}{point.partial && <span className="block text-xs text-muted-foreground">Partial interval</span>}</TableCell>
              <TableCell>{displaySeconds(point.p50)}</TableCell><TableCell>{displaySeconds(point.p95)}</TableCell><TableCell>{point.samples}</TableCell><TableCell>{point.count}</TableCell><TableCell>{point.error_count}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </div>
      </details>
    </>}
  </section>;
};

export default RoleplayPerformanceTrends;
